/* eslint-disable @typescript-eslint/no-explicit-any */
import { Layer, LayerOption, LayerSymbol } from './types';
import { useScript } from '@ttoss/hooks';
import React from 'react';

export type SiflorMapContextType = {
  getLayers: () => Layer[];
  loadLayer: (args: LayerOption) => void;
  unloadLayer: (selectedLayer: LayerOption) => void;
  getLayerSymbols: (selectedLayer: LayerOption) => LayerSymbol[];
  init: (map: google.maps.Map) => void;
  isSiflorMapLoaded?: boolean;
};

export const SiflorMapContext = React.createContext<SiflorMapContextType>({
  getLayers: () => {
    return [];
  },
  loadLayer: () => {
    return null;
  },
  unloadLayer: () => {
    return null;
  },
  init: () => {
    return null;
  },
  getLayerSymbols: () => {
    return [];
  },
});

/**
 * We created this provider to be able to use the init and getLayers functions,
 * which are inside the siflorMap that is injected into window.
 */

export const SiflorMapProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const url =
    'https://bkt-siflor-static-dev.s3.sa-east-1.amazonaws.com/js/siflormaps.js';

  const { status } = useScript(url);

  const isSiflorMapLoaded = React.useMemo(() => {
    return status === 'ready';
  }, [status]);

  const siflorMap = React.useMemo(() => {
    if (isSiflorMapLoaded) {
      return (window as any).siflorMap;
    }
  }, [isSiflorMapLoaded]);

  const [mapInitialized, setMapInitialized] = React.useState(false);

  const init = React.useCallback(
    async (map: google.maps.Map) => {
      if (siflorMap) {
        try {
          await siflorMap.init(map);
          setMapInitialized(true);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('error on init siflorMap', error);
        }
      }
    },
    [siflorMap]
  );

  const getLayers = React.useCallback((): Layer[] => {
    if (!siflorMap || !mapInitialized) {
      return [];
    }

    const layersSiflor = siflorMap.getLayers() as {
      [key: string]: {
        code: string;
        name: string;
        layers: { [key: string]: { name: string; code: string } };
      };
    };

    const layersMapping = Object.values(layersSiflor)
      .map((item) => {
        const options = Object.values(item.layers).map((layer) => {
          return layer;
        });

        return { group: item.name, code: item.code, options };
      })
      /**
       * Doesn't display geral (contour) layer.
       */
      .filter((item) => {
        return item.code !== 'geral';
      });

    return layersMapping;
  }, [siflorMap, mapInitialized]);

  const loadLayer = React.useCallback(
    ({ groupCode, layerCode }: LayerOption) => {
      if (!siflorMap) {
        return;
      }

      siflorMap.loadLayer(groupCode, layerCode);
    },
    [siflorMap]
  );

  const unloadLayer = React.useCallback(
    ({ groupCode, layerCode }: LayerOption) => {
      if (!siflorMap) {
        return;
      }

      siflorMap.unloadLayer(groupCode, layerCode);
    },
    [siflorMap]
  );

  const getLayerSymbols = React.useCallback(
    ({ groupCode, layerCode }: LayerOption) => {
      if (!siflorMap) {
        return [];
      }

      return siflorMap.getLayerSymbols(groupCode, layerCode);
    },
    [siflorMap]
  );

  return (
    <SiflorMapContext.Provider
      value={{
        getLayers,
        init,
        isSiflorMapLoaded,
        loadLayer,
        unloadLayer,
        getLayerSymbols,
      }}
    >
      {children}
    </SiflorMapContext.Provider>
  );
};
